/** The three screens an email link lands on.
 *
 * They share a shape — one centred glass card, one outcome — so they share a
 * file and a stylesheet. All three render before the sign-in gate in `App.tsx`,
 * because a link opened in the mail client's browser has no session and the
 * token in the URL is the only credential involved.
 */

import { useEffect, useRef, useState } from 'react'
import { ApiError, api, setToken } from '../lib/api'
import { stashInvite, stripQuery } from '../lib/linkRoute'
import { navigate } from '../lib/pathname'
import { useStore } from '../lib/store'
import './LinkLanding.css'

type State = 'working' | 'ok' | 'failed'

function Card({
  state,
  title,
  children,
}: {
  state: State
  title: string
  children: React.ReactNode
}) {
  return (
    <div className="ll">
      <div className="ll__card glass">
        <Mark state={state} />
        <h1>{title}</h1>
        {children}
      </div>
    </div>
  )
}

function Mark({ state }: { state: State }) {
  return (
    <span className={`ll__mark ll__mark--${state}`} aria-hidden="true">
      {state === 'working' && <span className="ll__spin" />}
      {state === 'ok' && (
        <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
          <path d="M4 12.5l5.5 5.5L20 7" />
        </svg>
      )}
      {state === 'failed' && (
        <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 7v6.5M12 17.2v.1" />
        </svg>
      )}
    </span>
  )
}

/** `/verify?token=…` */
export function VerifyEmail({ token }: { token: string | null }) {
  const { user, refreshUser } = useStore()
  const [state, setState] = useState<State>(token ? 'working' : 'failed')
  const [message, setMessage] = useState(
    token ? '' : 'That link is missing its token. Try opening it from the email again.',
  )
  // StrictMode double-invokes effects in dev; confirming twice is harmless
  // server-side but would flash the "already confirmed" copy for no reason.
  const done = useRef(false)

  useEffect(() => {
    if (!token || done.current) return
    done.current = true
    void (async () => {
      try {
        const result = await api.confirmEmail(token)
        stripQuery()
        setState('ok')
        setMessage(
          result.already_verified
            ? 'This address was already confirmed. Nothing else to do.'
            : `Thanks${result.display_name ? `, ${result.display_name}` : ''} — your address is confirmed.`,
        )
        // Refresh the cached user so the nag banner disappears behind them.
        if (user) void refreshUser()
      } catch (e) {
        setState('failed')
        setMessage(e instanceof ApiError ? e.message : String(e))
      }
    })()
  }, [token, user, refreshUser])

  return (
    <Card state={state} title={state === 'ok' ? 'Email confirmed' : 'Confirming your email'}>
      {message && <p>{message}</p>}
      {state !== 'working' && (
        <button className="btn" onClick={() => navigate('/')}>
          {user ? 'Back to Nudge' : 'Open Nudge'}
        </button>
      )}
    </Card>
  )
}

/** `/reset?token=…` */
export function ResetPassword({ token }: { token: string | null }) {
  const { refreshUser } = useStore()
  const [password, setPassword] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(
    token ? null : 'That link is missing its token. Request a new one from the sign-in screen.',
  )
  const [done, setDone] = useState(false)

  async function submit() {
    if (!token) return
    setBusy(true)
    setError(null)
    try {
      const { access_token } = await api.resetPassword(token, password)
      // The reset proves the mailbox, so the server hands back a session and we
      // sign them straight in rather than asking for the password just set.
      setToken(access_token)
      stripQuery()
      setDone(true)
      await refreshUser()
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  if (done) {
    return (
      <Card state="ok" title="Password changed">
        <p>You&rsquo;re signed in on this device.</p>
        <button className="btn" onClick={() => navigate('/')}>
          Open Nudge
        </button>
      </Card>
    )
  }

  return (
    <Card state={error && !token ? 'failed' : 'working'} title="Choose a new password">
      {token && <p>Pick something you don&rsquo;t use anywhere else.</p>}
      {token && (
        <input
          className="ll__input"
          type="password"
          placeholder="New password (8+ characters)"
          value={password}
          autoComplete="new-password"
          autoFocus
          onChange={(e) => setPassword(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && password.length >= 8 && void submit()}
        />
      )}
      {error && <p className="ll__error">{error}</p>}
      {token ? (
        <button className="btn" disabled={busy || password.length < 8} onClick={submit}>
          {busy ? 'Saving…' : 'Set password'}
        </button>
      ) : (
        <button className="btn" onClick={() => navigate('/')}>
          Back to sign in
        </button>
      )}
    </Card>
  )
}

/** `/join?code=…` — the household invite landing.
 *
 * Signed in, it joins and drops them on the Household tab. Signed out, it parks
 * the code in session storage and shows sign-in; `App` picks it up afterwards.
 */
export function JoinInvite({ code }: { code: string | null }) {
  const { user, refreshHouseholds, setTab } = useStore()
  const [state, setState] = useState<State>(code ? 'working' : 'failed')
  const [message, setMessage] = useState(
    code ? '' : 'That invite link is missing its code. Ask for a new one.',
  )
  const done = useRef(false)

  useEffect(() => {
    if (!code || done.current) return
    if (!user) {
      stashInvite(code)
      navigate('/')
      return
    }
    done.current = true
    void (async () => {
      try {
        const household = await api.joinHousehold(code)
        await refreshHouseholds()
        setState('ok')
        setMessage(`You're in — welcome to ${household.name}.`)
      } catch (e) {
        setState('failed')
        setMessage(e instanceof ApiError ? e.message : String(e))
      }
    })()
  }, [code, user, refreshHouseholds])

  return (
    <Card state={state} title={state === 'ok' ? 'Joined' : 'Joining household'}>
      {message && <p>{message}</p>}
      {state !== 'working' && (
        <button
          className="btn"
          onClick={() => {
            setTab('household')
            navigate('/')
          }}
        >
          Open the household
        </button>
      )}
    </Card>
  )
}
